import { Store, AlertCircle, RefreshCcw } from 'lucide-react';
import { twMerge } from 'tailwind-merge';
import { StatCard } from '@/components/stat-card';
import { useAmazonPage } from './use-amazon-page';

const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  PLANNED: { label: 'Planeado', className: 'badge-info' },
  ACTIVE: { label: 'Activo', className: 'badge-success' },
  INACTIVE: { label: 'Inactivo', className: 'badge-neutral' },
  PROCESSING: { label: 'Procesando', className: 'badge-warning' },
  NONE: { label: 'Sin config', className: 'badge-ghost' },
};

const ACTION_LABELS: Record<number, string> = {
  1: 'Planear',
  2: 'Activar',
  3: 'Desactivar',
  4: 'Actualizar',
};

export function AmazonPage() {
  const {
    places,
    counter,
    isLoading,
    isError,
    selected_ids,
    available_actions,
    is_confirm_modal_open,
    is_processing,
    onToggleSelect,
    onSelectAll,
    onInitiateAction,
    onConfirmAction,
    onCloseModal,
  } = useAmazonPage();

  if (isLoading) {
    return (
      <div className="flex justify-center p-16">
        <span className="loading loading-spinner loading-lg text-primary" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="alert alert-error">
        <AlertCircle className="h-5 w-5" />
        <span>Error al cargar los datos de Amazon Hub.</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-1">
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <Store className="h-8 w-8 text-primary" />
          Amazon Hub
        </h1>
        <p className="text-base-content/70">
          Gestión de puntos de entrega para Amazon Access Point.
        </p>
      </header>

      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Peticiones (24h)"
          value={`${counter?.requests_number ?? 0}`}
          hint={`Ventana de ${counter?.window_hours ?? 24} horas`}
          Icon={RefreshCcw}
          tone="info"
        />
      </section>

      <div className="flex flex-col gap-4">
        {/* Bulk Action Bar */}
        {selected_ids.length > 0 && (
          <div className="alert bg-base-300 border-base-content/10 shadow-lg flex justify-between items-center py-2 px-4 sticky top-0 z-10">
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium">
                {selected_ids.length} puntos seleccionados
              </span>
              <div className="flex gap-2">
                {[1, 2, 3, 4].map((action) => (
                  <button
                    key={action}
                    disabled={!available_actions.has(action)}
                    onClick={() => onInitiateAction(action)}
                    className={twMerge(
                      "btn btn-xs",
                      available_actions.has(action) ? "btn-primary" : "btn-disabled"
                    )}
                  >
                    {ACTION_LABELS[action]}
                  </button>
                ))}
              </div>
            </div>
            <button className="btn btn-ghost btn-xs" onClick={() => onSelectAll(false)}>
              Limpiar
            </button>
          </div>
        )}

        <div className="overflow-x-auto rounded-box border border-base-300">
          <table className="table table-zebra w-full">
            <thead>
              <tr>
                <th className="w-12">
                  <input
                    type="checkbox"
                    className="checkbox checkbox-sm"
                    checked={selected_ids.length === places.length && places.length > 0}
                    onChange={(e) => onSelectAll(e.target.checked)}
                  />
                </th>
                <th>Punto de despacho</th>
                <th>Ciudad / País</th>
                <th>Estado Amazon</th>
                <th>Feed ID</th>
              </tr>
            </thead>
            <tbody>
              {places.map((place) => {
                const statusKey = place.amazon?.processing_status === 1
                  ? 'PROCESSING'
                  : (place.amazon?.status || 'NONE');
                const config = STATUS_CONFIG[statusKey];

                return (
                  <tr key={place.id}>
                    <td>
                      <input
                        type="checkbox"
                        className="checkbox checkbox-sm"
                        checked={selected_ids.length > 0 && selected_ids.includes(place.id)}
                        onChange={() => onToggleSelect(place.id)}
                      />
                    </td>
                    <td>
                      <div className="font-bold">{place.name}</div>
                      <div className="text-xs opacity-50">{place.id}</div>
                    </td>
                    <td>
                      {place.address?.city} · {place.country_code.toUpperCase()}
                    </td>
                    <td>
                      <span className={twMerge('badge badge-sm', config.className)}>
                        {config.label}
                      </span>
                    </td>
                    <td className="font-mono text-xs">
                      {place.amazon?.feed_id || '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Confirmation Modal */}
      {is_confirm_modal_open && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg">Confirmar acción</h3>
            <p className="py-4">
              ¿Estás seguro que deseas aplicar la acción <strong>{ACTION_LABELS[is_confirm_modal_open ? 1 : 1]}</strong> a {selected_ids.length} puntos?
            </p>
            <div className="modal-action">
              <button className="btn" onClick={onCloseModal} disabled={is_processing}>
                Cancelar
              </button>
              <button
                className="btn btn-primary"
                onClick={onConfirmAction}
                disabled={is_processing}
              >
                {is_processing && <span className="loading loading-spinner loading-xs" />}
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
